/**
 * Global scroll position management for the entire application
 */

import { set } from "date-fns";

// Store scroll positions by route path
const scrollPositions = {};
let isHandlingNavigation = false;
let lastKnownScrollPosition = 0;
let activeScrollMonitor = null;
let restorationAttempts = {};
let lastNavigationTimestamp = 0;

// Track whether we're navigating back or forward
let isBackNavigation = false;
let navigationStartPath = '';

// Set up global scroll tracking
export const initializeGlobalScrollTracking = () => {
	// Track history state directly for more reliable back detection
	let lastAction = '';

	// Get window.history in a way that doesn't trigger ESLint warnings
	const windowHistory = window.history;
	const originalPushState = windowHistory.pushState;
	const originalReplaceState = windowHistory.replaceState;

	// Override native history methods to track navigation type
	windowHistory.pushState = function (state, title, url) {
		lastAction = 'push';
		originalPushState.apply(this, arguments);
	};

	windowHistory.replaceState = function (state, title, url) {
		lastAction = 'replace';
		originalReplaceState.apply(this, arguments);
	};

	// Setup route change detection
	document.addEventListener('ionRouteDidChange', (event) => {
		const previousPath = event.detail.from;
		const currentPath = event.detail.to;

		if (isHandlingNavigation) return;

		// Mark that we're handling a navigation to prevent duplicate events
		isHandlingNavigation = true;

		// Set up back navigation detection
		const isBack = (lastAction !== 'push' && isBackNavigation) || // Our popstate detection
			event.detail.direction === 'back' || // Ionic's direction info
			(window.performance && window.performance.navigation.type === 2);

		// Save the scroll position for the page we're leaving
		if (previousPath) {
			// Use the last known position if it's available and non-zero
			if (lastKnownScrollPosition > 0) {
				//console.log(`[Global Scroll] Saving last known position for ${previousPath}: ${lastKnownScrollPosition}px`);
				//localStorage.setItem(`scrollPosition_${previousPath}`, lastKnownScrollPosition.toString());
				sessionStorage.setItem(`scrollPosition_${previousPath}`, lastKnownScrollPosition.toString());
				scrollPositions[previousPath] = lastKnownScrollPosition;
			} else {
				saveScrollPositionForPath(previousPath);
			}
		}

		// Clear the scroll monitor when leaving a page
		clearScrollMonitor();

		//console.log(`[Global Scroll] Navigation: ${previousPath} -> ${currentPath}, isBack: ${isBack}, lastAction: ${lastAction}`);

		// Reset restoration attempts for this path
		restorationAttempts[currentPath] = 0;

		// Only restore scroll position if this is a back navigation or we have a directly saved position
		if (currentPath && (isBack ||
			sessionStorage.getItem(`scrollPosition_directSave_${currentPath}`) === 'true')) {
			// Get saved position now, before attempting to restore
			const savedPosition = parseInt(
				//localStorage.getItem(`scrollPosition_${currentPath}`) ||
				sessionStorage.getItem(`scrollPosition_${currentPath}`) || '0');

			if (savedPosition > 0) {
				//console.log(`[Global Scroll] Will attempt to restore ${currentPath} to ${savedPosition}px`);

				// Try immediately and then with increasing delays
				restoreScrollPositionForPath(currentPath);

				// Use multiple delays for better reliability
				[100, 300, 600, 1000, 1500, 2000, 3000].forEach(delay => {
					setTimeout(() => {
						if (restorationAttempts[currentPath] < 8) { // Limit attempts
							restoreScrollPositionForPath(currentPath);
							restorationAttempts[currentPath]++;
						}
					}, delay);
				});
			} else {
				// No saved position, scroll to top
				scrollToTop();
			}
		} else {
			// For forward navigation to new page, scroll to top
			//console.log(`[Global Scroll] Forward navigation to ${currentPath}, scrolling to top`);
			scrollToTop();
		}

		// Reset flags
		isBackNavigation = false;
		lastAction = '';
		sessionStorage.removeItem(`scrollPosition_directSave_${currentPath}`);

		// Reset the navigation handling flag after a delay
		setTimeout(() => {
			isHandlingNavigation = false;

			// Set up continuous monitoring for the new page
			setupScrollMonitor();
		}, 1200);
	});

	// Listen for back button events
	window.addEventListener('popstate', (event) => {
		isBackNavigation = true;
		lastAction = 'pop';
		lastNavigationTimestamp = Date.now();
		//console.log('[Global Scroll] Back navigation detected');

		// Save the current scroll position before going back
		const currentPath = window.location.pathname;
		if (lastKnownScrollPosition > 0) {
			// Save the position with a special flag for backnavigation
			//localStorage.setItem(`scrollPosition_${currentPath}`, lastKnownScrollPosition.toString());
			sessionStorage.setItem(`scrollPosition_${currentPath}`, lastKnownScrollPosition.toString());
			sessionStorage.setItem(`scrollPosition_isBack_${currentPath}`, 'true');
			sessionStorage.setItem(`scrollPosition_timestamp_${currentPath}`, lastNavigationTimestamp.toString());
			scrollPositions[currentPath] = lastKnownScrollPosition;

			// Also save with a timestamp for debugging
			//localStorage.setItem(`scrollPosition_${currentPath}_${lastNavigationTimestamp}`, lastKnownScrollPosition.toString());

			//console.log(`[Global Scroll] Saved position on back navigation: ${lastKnownScrollPosition}px`);
		}
	});

	// Monitor page transitions to ensure scroll restoration works
	document.addEventListener('ionPageDidEnter', (event) => {
		const path = window.location.pathname;

		// Get the most recent saved position for this path
		const savedPosition = parseInt(
			//localStorage.getItem(`scrollPosition_${path}`) ||
			sessionStorage.getItem(`scrollPosition_${path}`) || '0');

		// If we have a saved position, this is a back navigation, or 
		// this path was marked for direct restoration
		if (savedPosition > 0 && (
			sessionStorage.getItem(`scrollPosition_isBack_${path}`) === 'true' ||
			isBackNavigation ||
			sessionStorage.getItem(`scrollPosition_directSave_${path}`) === 'true')) {

			//console.log(`[Global Scroll] Page entered, attempting to restore to ${savedPosition}px`);

			// Capture the current content element for stable reference
			const contentEl = event.target.querySelector('ion-content');

			// Function to apply scroll with verification
			const applyAndVerify = async () => {
				if (!contentEl) return false;

				// Try direct DOM access first
				if (contentEl.shadowRoot) {
					const scrollEl = contentEl.shadowRoot.querySelector('.inner-scroll');
					if (scrollEl) {
						// Record initial position for debugging
						const beforePos = scrollEl.scrollTop;

						// Apply position
						scrollEl.scrollTop = savedPosition;

						// Verify after a short delay
						setTimeout(() => {
							const afterPos = scrollEl.scrollTop;
							//console.log(`[Global Scroll] Position change: ${beforePos} → ${afterPos} (target: ${savedPosition})`);

							// If not close enough to target, try again
							if (Math.abs(afterPos - savedPosition) > 10) {
								//console.log('[Global Scroll] Position not accurate, trying again');
								scrollEl.scrollTop = savedPosition;

								// Also try Ionic's API as backup
								try {
									contentEl.scrollToPoint(0, savedPosition, 0);
								} catch (e) { }
							}
						}, 50);

						return true;
					}
				}

				// Try Ionic's API if direct DOM failed
				try {
					await contentEl.scrollToPoint(0, savedPosition, 0);
					return true;
				} catch (e) {
					return false;
				}
			};

			// Try to scroll immediately
			applyAndVerify();

			// Also try with multiple increasing delays for reliability
			[100, 300, 500, 800, 1200, 2000].forEach(delay => {
				setTimeout(applyAndVerify, delay);
			});

			// Clear flags after a reasonable time
			setTimeout(() => {
				sessionStorage.removeItem(`scrollPosition_isBack_${path}`);
				sessionStorage.removeItem(`scrollPosition_directSave_${path}`);
			}, 5000);
		}
	});

	// Also add explicit listener for direct DOM mutations with enhanced capabilities
	const bodyObserver = new MutationObserver((mutations) => {
		// Get current path and whether we're in a restoration state
		const currentPath = window.location.pathname;
		const isRestorationNeeded =
			isBackNavigation ||
			sessionStorage.getItem(`scrollPosition_isBack_${currentPath}`) === 'true' ||
			sessionStorage.getItem(`scrollPosition_directSave_${currentPath}`) === 'true';

		// If restoration is needed, look for content elements
		if (isRestorationNeeded) {
			// Get saved position
			const savedPosition = parseInt(
				//localStorage.getItem(`scrollPosition_${currentPath}`) ||
				sessionStorage.getItem(`scrollPosition_${currentPath}`) || '0');

			if (savedPosition > 0) {
				// Process mutation
				mutations.forEach(mutation => {
					if (mutation.type === 'childList') {
						// Check added nodes for ion-content or nodes that might contain ion-content
						mutation.addedNodes.forEach(node => {
							if (node.nodeType === Node.ELEMENT_NODE) {
								// Try to find ion-content elements
								const contentEls = node.querySelectorAll ?
									node.querySelectorAll('ion-content') : [];

								// If we find content elements, restore scroll
								if (contentEls.length > 0) {
									//console.log(`[Global Scroll] Found ${contentEls.length} content elements via mutation observer`);

									// For each content element, try to restore scroll
									contentEls.forEach(content => {
										//console.log(`[Global Scroll] Applying scroll to ${savedPosition}px via mutation observer`);

										// Try direct DOM access first
										if (content.shadowRoot) {
											const scrollEl = content.shadowRoot.querySelector('.inner-scroll');
											if (scrollEl) {
												// Apply with logging
												const beforePos = scrollEl.scrollTop;
												scrollEl.scrollTop = savedPosition;

												// Log the result
												setTimeout(() => {
													const afterPos = scrollEl.scrollTop;
													//console.log(`[Global Scroll] Mutation observer scroll: ${beforePos} → ${afterPos} (target: ${savedPosition})`);
												}, 50);
											}
										}

										// Also try using Ionic's API
										if (typeof content.scrollToPoint === 'function') {
											[1, 50, 200, 1000, 2000].forEach(delay => {
												setTimeout(() => {
													content.scrollToPoint(0, savedPosition, 0).catch((e) => { 
														console.warn('[Global Scroll] Mutation observer scrollToPoint failed :', e);
													});
												}, delay);
											});
										}
									});
								} else if (node.tagName === 'ION-CONTENT') {
									// The node itself is an ion-content
									//console.log('[Global Scroll] Found direct ion-content via mutation observer');

									// Try direct DOM access
									if (node.shadowRoot) {
										const scrollEl = node.shadowRoot.querySelector('.inner-scroll');
										if (scrollEl) {
											scrollEl.scrollTop = savedPosition;
										}
									}

									// Try Ionic's API
									if (typeof node.scrollToPoint === 'function') {
										node.scrollToPoint(0, savedPosition, 0).catch(() => { });
									}
								}
							}
						});
					}
				});
			}
		}
	});

	// Start observing the body element for child additions/removals
	bodyObserver.observe(document.body, { childList: true, subtree: true });

	// Handle page visibility changes (like app switching)
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') {
			// Save scroll position when the app goes to background
			const currentPath = window.location.pathname;
			if (lastKnownScrollPosition > 0) {
				sessionStorage.setItem(`scrollPosition_${currentPath}`, lastKnownScrollPosition.toString());
			}
		}
	});

	// Also handle regular page navigation
	window.addEventListener('beforeunload', () => {
		const currentPath = window.location.pathname;
		if (lastKnownScrollPosition > 0) {
			sessionStorage.setItem(`scrollPosition_${currentPath}`, lastKnownScrollPosition.toString());
		} else {
			saveScrollPositionForPath(currentPath);
		}
	});

	// Set up manual capturing when clicking links
	setupClickCapture();

	// Start monitoring scroll position
	setupScrollMonitor();

	//console.log('[Global Scroll] Initialized');
};

// Set up continuous monitoring of scroll position
const setupScrollMonitor = () => {
	// Clear any existing monitor
	clearScrollMonitor();

	// Set up a new monitor
	activeScrollMonitor = setInterval(() => {
		captureCurrentScrollPosition();
	}, 500);
};

// Clear the scroll monitor
const clearScrollMonitor = () => {
	if (activeScrollMonitor) {
		clearInterval(activeScrollMonitor);
		activeScrollMonitor = null;
	}
};

// Capture the current scroll position
const captureCurrentScrollPosition = () => {
	const currentPage = document.querySelector('ion-page:not(.ion-page-hidden)');
	if (!currentPage) return;

	const contentEl = currentPage.querySelector('ion-content');
	if (!contentEl) return;

	// Method 1: Try shadow DOM access (most reliable for Ionic)
	if (contentEl.shadowRoot) {
		const innerScroll = contentEl.shadowRoot.querySelector('.inner-scroll');
		if (innerScroll && innerScroll.scrollTop > 0) {
			lastKnownScrollPosition = innerScroll.scrollTop;
			return;
		}
	}

	// Method 2: Try using Ionic's API
	if (typeof contentEl.getScrollElement === 'function') {
		contentEl.getScrollElement().then(scrollEl => {
			if (scrollEl && scrollEl.scrollTop > 0) {
				lastKnownScrollPosition = scrollEl.scrollTop;
			}
		}).catch(err => {
			// Silent fail
		});
	}
};

// Scroll to top helper
const scrollToTop = () => {
	// Reset the last known position
	lastKnownScrollPosition = 0;

	document.querySelectorAll('ion-content').forEach(async (contentEl) => {
		try {
			if (typeof contentEl.scrollToTop === 'function') {
				await contentEl.scrollToTop(0); // No animation
			} else {
				const scrollEl = await contentEl.getScrollElement();
				if (scrollEl) {
					scrollEl.scrollTo({ top: 0, behavior: 'auto' });
				}
			}
		} catch (err) {
			//console.warn('[Global Scroll] Error scrolling to top:', err);
		}
	});
};

// Set up click capturing on navigation elements
const setupClickCapture = () => {
	// Look for navigation elements that might cause route changes
	document.addEventListener('click', (event) => {
		const link = event.target.closest('a, ion-button[routerlink], ion-item[routerlink]');
		if (link) {
			// Save current scroll position before navigation occurs
			const currentPath = window.location.pathname;
			navigationStartPath = currentPath;

			// Use the last known position if it's available and non-zero
			if (lastKnownScrollPosition > 0) {
				//console.log(`[Global Scroll] Saving position on click for ${currentPath}: ${lastKnownScrollPosition}px`);
				//localStorage.setItem(`scrollPosition_${currentPath}`, lastKnownScrollPosition.toString());
				sessionStorage.setItem(`scrollPosition_${currentPath}`, lastKnownScrollPosition.toString());
				scrollPositions[currentPath] = lastKnownScrollPosition;
			} else {
				saveScrollPositionForPath(currentPath);
			}
		}
	}, true); // Use capture phase to ensure we run before navigation
};

// Save scroll position for a specific path
const saveScrollPositionForPath = (path) => {
	if (!path) return;

	// Find active ion-content element first (most common case)
	const currentPage = document.querySelector('ion-page:not(.ion-page-hidden)');
	if (currentPage) {
		const contentEl = currentPage.querySelector('ion-content');
		if (contentEl) {
			captureScrollPosition(contentEl, path);
			return; // We found and processed the active content
		}
	}

	// Fallback: check all scroll-position-capture elements
	document.querySelectorAll('ion-content.scroll-position-capture').forEach(contentEl => {
		captureScrollPosition(contentEl, path);
	});
};

// Helper to capture scroll position from an ion-content element
const captureScrollPosition = async (contentEl, path) => {
	try {
		let scrollPosition = 0;

		// Method 1: Try shadow DOM access first (most reliable for Ionic)
		if (contentEl.shadowRoot) {
			const innerScroll = contentEl.shadowRoot.querySelector('.inner-scroll');
			if (innerScroll) {
				scrollPosition = innerScroll.scrollTop;
				//console.log(`[Method 2] Got scroll position for ${path}: ${scrollPosition}px`);
			}
		}

		// Method 2: Use Ionic's API if shadow DOM didn't work
		if (scrollPosition === 0 && typeof contentEl.getScrollElement === 'function') {
			try {
				const scrollEl = await contentEl.getScrollElement();
				if (scrollEl) {
					scrollPosition = scrollEl.scrollTop;
					//console.log(`[Method 1] Got scroll position for ${path}: ${scrollPosition}px`);
				}
			} catch (err) {
				console.warn('Method 1 failed:', err);
			}
		}

		// Method 3: Check if we have a lastKnownScrollPosition
		if (scrollPosition === 0 && lastKnownScrollPosition > 0) {
			scrollPosition = lastKnownScrollPosition;
			//console.log(`[Method 3] Using last known position for ${path}: ${scrollPosition}px`);
		}

        // Save the final scroll position
        scrollPositions[path] = scrollPosition;
        sessionStorage.setItem(`scrollPosition_${path}`, scrollPosition.toString());
	} catch (err) {
		console.warn(`[Global Scroll] Could not save scroll for ${path}:`, err);
	}
};

// Restore scroll position for a specific path
const restoreScrollPositionForPath = (path) => {
	if (!path) return;

	// Get saved position from all available sources
	let savedPosition = scrollPositions[path] ||
		parseInt(sessionStorage.getItem(`scrollPosition_${path}`) || '0') ||
		parseInt(sessionStorage.getItem(`scrollPosition_${path}`) || '0');

	if (savedPosition <= 0) return;

	//console.log(`[Global Scroll] Trying to restore ${path} to ${savedPosition}px (attempt ${restorationAttempts[path] || 0})`);

	// Find the active page first (most common case)
	const currentPage = document.querySelector('ion-page:not(.ion-page-hidden)');
	if (currentPage) {
		const contentEl = currentPage.querySelector('ion-content');
		if (contentEl) {
			const success = applyScrollPosition(contentEl, savedPosition);
			if (success) {
				//console.log(`[Global Scroll] Successfully restored scroll position to ${savedPosition}px`);
			}
			return;
		}
	}

	// Fallback: try all ion-content elements
	let restored = false;
	document.querySelectorAll('ion-content').forEach(contentEl => {
		if (!restored) {
			restored = applyScrollPosition(contentEl, savedPosition);
		}
	});
};

// Helper to apply scroll position to an ion-content element
const applyScrollPosition = async (contentEl, position) => {
	try {
		// Method 1: Direct Shadow DOM manipulation - with enhanced verification
		if (contentEl.shadowRoot) {
			const innerScroll = contentEl.shadowRoot.querySelector('.inner-scroll');
			if (innerScroll) {
				// Store original position to verify change
				const originalPos = innerScroll.scrollTop;

				// Apply new position
				innerScroll.scrollTop = position;
				//console.log(`[Method Direct] Applied scroll position using shadowRoot: ${originalPos} → ${position}px`);

				// Double-check if it worked (in case of scrollHeight constraints)
				setTimeout(() => {
					const currentPos = innerScroll.scrollTop;
					if (Math.abs(currentPos - position) > 10) {
						//console.log(`[Method Direct] Scroll position verification failed (current: ${currentPos}), retrying...`);

						// Try once more with direct access
						innerScroll.scrollTop = position;

						// Also try scrollIntoView as a more aggressive approach
						const elementNearTarget = findElementNearPosition(innerScroll, position);
						if (elementNearTarget) {
							//console.log('[Method Direct] Found element near target position, using scrollIntoView');
							elementNearTarget.scrollIntoView({ behavior: 'auto', block: 'start' });
						}

						// Final check
						setTimeout(() => {
							//console.log(`[Method Direct] Final position: ${innerScroll.scrollTop}px (target: ${position}px)`);
						}, 50);
					} else {
						//console.log(`[Method Direct] Scroll position verified: ${currentPos}px`);
					}
				}, 50);

				return true;
			}
		}

		// Method 2: Use Ionic's scrollToPoint method
		if (typeof contentEl.scrollToPoint === 'function') {
			try {
				// Disable animations for more reliable positioning
				await contentEl.scrollToPoint(0, position, 0);
				//console.log('[Method 1] Applied scroll position using scrollToPoint');
				return true;
			} catch (err) {
				console.warn('scrollToPoint failed:', err);
			}
		}

		// Method 3: Use Ionic's scrollByPoint method
		if (typeof contentEl.scrollByPoint === 'function') {
			try {
				// Get current position
				const scrollEl = await contentEl.getScrollElement();
				const currentPos = scrollEl ? scrollEl.scrollTop : 0;
				// Calculate difference and scroll by that amount
				const delta = position - currentPos;

				if (Math.abs(delta) > 5) { // Only scroll if there's a meaningful difference
					await contentEl.scrollByPoint(0, delta, 0);
					//console.log('[Method 2] Applied scroll position using scrollByPoint');
					return true;
				}
			} catch (err) {
				console.warn('scrollByPoint failed:', err);
			}
		}

		// Method 4: Direct scrollElement access
		try {
			const scrollEl = await contentEl.getScrollElement();
			if (scrollEl) {
				scrollEl.scrollTo({ top: position, behavior: 'auto' });
				//console.log('[Method 3] Applied scroll position using scrollElement.scrollTo');
				return true;
			}
		} catch (err) {
			console.warn('Direct scrollElement access failed:', err);
		}

		return false;
	} catch (err) {
		console.warn(`[Global Scroll] Could not restore scroll:`, err);
		return false;
	}
};

// Helper function to find an element near a specific scroll position
const findElementNearPosition = (scrollContainer, targetPos) => {
	const elements = scrollContainer.querySelectorAll('*');
	let closestElement = null;
	let closestDistance = Number.MAX_VALUE;

	// Look for the element closest to our target position
	Array.from(elements).forEach(el => {
		const rect = el.getBoundingClientRect();
		const elPos = rect.top + scrollContainer.scrollTop;
		const distance = Math.abs(elPos - targetPos);

		if (distance < closestDistance) {
			closestDistance = distance;
			closestElement = el;
		}
	});

	return closestElement;
};

// Clear all saved positions
export const clearAllScrollPositions = () => {
	// Clear memory cache
	Object.keys(scrollPositions).forEach(path => {
		delete scrollPositions[path];
		localStorage.removeItem(`scrollPosition_${path}`);
		sessionStorage.removeItem(`scrollPosition_${path}`);
	});
	//console.log('[Global Scroll] Cleared all scroll positions');
};

// Special function to handle ProductCard navigation
export const saveScrollBeforeNavigation = async () => {
	// Immediately save current scroll position with the latest known position
	const currentPath = window.location.pathname;

	// Use the last known scroll position if available
	if (lastKnownScrollPosition > 0) {
		//console.log(`[Global Scroll] Saving before navigation for ${currentPath}: ${lastKnownScrollPosition}px`);
		//localStorage.setItem(`scrollPosition_${currentPath}`, lastKnownScrollPosition.toString());
		sessionStorage.setItem(`scrollPosition_${currentPath}`, lastKnownScrollPosition.toString());
		scrollPositions[currentPath] = lastKnownScrollPosition;
	} else {
		// Fall back to the standard method
		saveScrollPositionForPath(currentPath);
	}

	// Mark the path we're navigating from for when we return
	sessionStorage.setItem(`scrollPosition_directSave_${currentPath}`, 'true');

	// Return true to allow navigation to continue
	return true;
};

// Force a page to scroll to top when it loads
export const forceScrollToTop = () => {
	// Reset position tracking
	lastKnownScrollPosition = 0;

	// Clear any stored position for current path
	const currentPath = window.location.pathname;
	sessionStorage.removeItem(`scrollPosition_${currentPath}`);
	sessionStorage.removeItem(`scrollPosition_directSave_${currentPath}`);
	sessionStorage.removeItem(`scrollPosition_isBack_${currentPath}`);
	delete scrollPositions[currentPath];

	// Perform the scroll to top
	setTimeout(scrollToTop, 100);
};

// Add a direct scroll event listener to the document
document.addEventListener('DOMContentLoaded', () => {
	// Add direct event listeners to capture scroll events
	document.addEventListener('scroll', () => {
		// This is a fallback for regular scroll events
		const scrollY = window.scrollY || document.documentElement.scrollTop;
		if (scrollY > 0) {
			lastKnownScrollPosition = scrollY;
		}
	}, true);
});
